import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Category } from '@chicare/shared';

import { cn } from '@/lib/cn';
import { PackBox } from '@/components/ui/PackBox';
import { useSectorLink } from '@/lib/sector';
import { useReducedMotion } from '@/lib/useReducedMotion';

/**
 * The category tiles, as one row that scrolls sideways.
 *
 * ── WHY A RAIL AND NOT A GRID ───────────────────────────────────────────────
 *
 * The grid was four across on a phone and six on a desktop, so it wrapped. The
 * moment the category count stopped being a multiple of six, the shop front
 * grew a ragged half-row — two tiles sitting alone under six, with four empty
 * columns beside them. Adding categories made it worse, and the arrangement
 * changed shape at every breakpoint.
 *
 * A rail takes twenty categories as happily as six, looks the same at every
 * width, and tells a buyer there is more than fits on screen.
 *
 * ── THE DRIFT, AND EVERY REASON IT STOPS ────────────────────────────────────
 *
 * It moves right to left, slowly, so somebody notices the row continues. It
 * stops on hover, on touch, on keyboard focus anywhere inside, while a drag is
 * in progress, while the tab is hidden, and entirely for reduced motion.
 *
 * All of those are the same rule: a tile that slides away from a thumb already
 * reaching for it is worse than a tile that never moved, because the person
 * learns not to reach.
 *
 * ── WHY THE TILES ARE DUPLICATED ────────────────────────────────────────────
 *
 * A marquee that reaches the end and jumps back is a jolt. The list is rendered
 * twice and the scroll position wraps at the halfway mark, which is seamless.
 *
 * The copies are `aria-hidden` with `tabIndex={-1}`: a screen reader must not
 * read every category twice, and tabbing must not walk through a second set of
 * links that lead to the same places.
 */

interface Props {
  categories: Category[];
  art: 'photo' | 'drawn';
  drift: boolean;
  secondsPerTile: number;
  className?: string;
}

export function CategoryRail({ categories, art, drift, secondsPerTile, className }: Props) {
  const reduced = useReducedMotion();
  const rail = useRef<HTMLDivElement>(null);
  const [held, setHeld] = useState(false);

  // Only worth duplicating when there is actually something to scroll past.
  // Six categories on a wide desktop fit, and a drifting row that loops three
  // tiles is a fidget rather than an invitation.
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const node = rail.current;

    if (!node) {
      return;
    }

    const measure = () => setOverflows(node.scrollWidth > node.clientWidth + 8);

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);

    return () => observer.disconnect();
  }, [categories.length]);

  const looping = drift && !reduced && overflows && categories.length > 2;

  // ── The drift ───────────────────────────────────────────────────────────
  //
  // requestAnimationFrame rather than a CSS animation, because the row must
  // also be scrollable by hand: a transform would fight a thumb, and a CSS
  // animation cannot be paused mid-frame and resumed from where a drag left
  // the scroll position.
  useEffect(() => {
    const node = rail.current;

    if (!node || !looping || held) {
      return;
    }

    // One tile per `secondsPerTile`. Measured from a real tile so the speed
    // means the same thing whatever size the tiles are at this width.
    const tile = node.firstElementChild?.firstElementChild as HTMLElement | null;
    const tileWidth = tile?.offsetWidth ?? 120;
    const pxPerMs = tileWidth / (Math.max(2, secondsPerTile) * 1000);

    let frame = 0;
    let last = performance.now();

    // ────────────────── THE POSITION IS KEPT HERE, NOT ON THE ELEMENT ──────────────────
    //
    // `node.scrollLeft += 0.28` does nothing. The browser rounds scrollLeft to
    // a whole pixel on write, so reading it back gives the same integer and
    // the next frame adds 0.28 to that same number again. The rail sits
    // perfectly still while the animation loop runs at sixty frames a second.
    //
    // Accumulating in a float here and assigning the absolute value means the
    // fractions add up, and a slow drift is ENTIRELY made of fractions: at
    // five seconds per tile this moves about a quarter of a pixel per frame.
    let position = node.scrollLeft;

    const step = (now: number) => {
      const elapsed = now - last;
      last = now;

      // Somebody dragged it. Take their position as the truth rather than
      // yanking the rail back to where the drift had got to.
      if (Math.abs(node.scrollLeft - position) > 2) {
        position = node.scrollLeft;
      }

      // A backgrounded tab delivers one enormous frame on return. Clamping
      // stops the rail leaping half its length the moment somebody comes back.
      if (!document.hidden && elapsed < 200) {
        position += pxPerMs * elapsed;

        // The seamless wrap. Half the content is the duplicate, so resetting
        // by exactly half lands on an identical frame.
        const half = node.scrollWidth / 2;

        if (position >= half) {
          position -= half;
        }

        node.scrollLeft = position;
      }

      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame);
  }, [looping, held, secondsPerTile, categories.length]);

  const tiles = looping ? [...categories, ...categories] : categories;

  return (
    <nav
      aria-label="Categories"
      data-testid="category-grid"
      className={cn('relative', className)}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      // Keyboard focus holds it too: somebody tabbing along the row must not
      // have the next tile slide out from under them.
      onFocus={() => setHeld(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setHeld(false);
        }
      }}
    >
      <div
        ref={rail}
        data-testid="category-rail"
        // `scroll-smooth` is deliberately absent: it fights the per-frame
        // scrollLeft the drift sets, and the result stutters.
        className="no-scrollbar flex gap-2 overflow-x-auto overscroll-x-contain pt-3.5"
        style={{ scrollbarWidth: 'none' }}
        onPointerDown={() => setHeld(true)}
        onPointerUp={() => setHeld(false)}
        onPointerCancel={() => setHeld(false)}
        // A touch scroll ends without a pointerup in some browsers, so the
        // scroll itself releases the hold after it settles.
        onTouchEnd={() => setHeld(false)}
      >
        {tiles.map((category, index) => {
          const clone = index >= categories.length;

          return (
            <div key={clone ? `clone-${category.id}` : category.id} className="shrink-0">
              <Tile category={category} art={art} clone={clone} />
            </div>
          );
        })}
      </div>
    </nav>
  );
}

function Tile({
  category,
  art,
  clone,
}: {
  category: Category;
  art: 'photo' | 'drawn';
  clone: boolean;
}) {
  const link = useSectorLink();
  const [failed, setFailed] = useState(false);

  const showPhoto = art === 'photo' && category.image && !failed;

  /**
   * Where this tile goes.
   *
   * ────────────────── WHY A TILE MAY NOT BE A CATEGORY ──────────────────
   *
   * Every tile pointed at its own shelf for as long as the rail existed, which
   * was never a decision — it was the only thing a tile could mean when a drug
   * catalogue was the only thing on ChiCare. There is now a prescription board,
   * a pack verifier, a blog and a seller registration, and these six squares
   * are the most valuable in the app.
   *
   * So an admin may point one somewhere else, and a tile with nothing chosen
   * behaves exactly as it always has. No migration, no backfill, no change for
   * anybody who does not ask for it.
   *
   * ────────────────── AND WHY sectorLink AND NOT sectorPath ──────────────────
   *
   * `sectorPath` prefixes /vet unconditionally, which is right everywhere it is
   * called from code — the author knows the path has a twin. This path came out
   * of a text box in an admin console and may be `/support`, which has no
   * veterinary twin at all. `sectorLink` prefixes the paths that do.
   */
  const to = link(category.link_path || `/search?category=${category.slug}`);

  return (
    <Link
      to={to}
      data-testid={clone ? undefined : `category-tile-${category.slug}`}
      // The duplicate exists only so the loop is seamless. A screen reader
      // must not read every category twice, and tabbing must not walk a second
      // set of links to the same places.
      aria-hidden={clone || undefined}
      tabIndex={clone ? -1 : undefined}
      // Fixed widths rather than a fraction of the viewport: a rail whose
      // tiles resize as it scrolls is a rail that never settles. These are
      // close to the old grid's columns at each width.
      className="group block w-[88px] text-center no-underline sm:w-[104px] xl:w-[120px]"
    >
      {showPhoto ? (
        <div className="relative aspect-square overflow-hidden rounded-lg bg-paper">
          <img
            src={category.image!}
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            // The API returning a URL is not the same as that URL serving a
            // file. Without this a missing storage link draws an empty grey
            // square, which reads as a failed upload.
            onError={() => setFailed(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.07]"
          />
        </div>
      ) : (
        <PackBox pack={packForCategory(category.slug)} square rounded="lg" />
      )}

      <div className="mt-1.5 truncate text-[11px] font-semibold text-ink">{category.name}</div>

      {/* ── THE COUNT DESCRIBES A SHELF, SO ONLY A SHELF GETS ONE ──────
          A tile pointed somewhere else is not a shelf, and the count under it
          is not merely redundant — it is wrong in the most discouraging way
          available. A tile called "Send a prescription" has no products filed
          under it, so the count is 0, so it read "Coming soon": the shop front
          telling buyers that a feature which has worked for months is not
          ready yet. */}
      {typeof category.product_count === 'number' && !category.link_path ? (
        <div className="truncate text-[10px] text-faint">
          {category.product_count === 0
            ? 'Coming soon'
            : `${category.product_count} ${category.product_count === 1 ? 'product' : 'products'}`}
        </div>
      ) : null}
    </Link>
  );
}

/** Category tiles reuse the pack art rather than shipping six more icons. */
function packForCategory(slug: string) {
  switch (slug) {
    case 'equipment':
    case 'vet-equipment':
    case 'vet-farm-equipment':
      return 'bp' as const;
    case 'wellness':
    case 'vet-nutrition':
      return 'vit' as const;
    case 'baby-mother':
    case 'vet-antiparasitics':
      return 'syrup' as const;
    case 'first-aid':
      return 'firstaid' as const;
    case 'chronic-care':
      return 'inhaler' as const;
    case 'vet-vaccines':
    case 'vet-clinical':
    case 'vet-dosing':
      return 'test' as const;
    default:
      return 'para' as const;
  }
}
